import { useEffect, useState } from 'react';
import {
    Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField, Typography
} from '@mui/material';
import i18n from '../translationUtils/i18n';


export default function PhoneVerificationDialog({
    open,
    email,
    phone,
    preferredLanguage,
    axiosInstance,
    initialMethod = 'sms',
    initialConsent = false,
    showMethodPicker = false,
    allowClose = true,
    closeLabel,
    extraVerifyPayload = {},
    onVerified,
    onClose
}) {
    // idle -> sent -> verified, with sendFailed / rejected as recoverable detours.
    const [status, setStatus] = useState('idle');
    const [method, setMethod] = useState(initialMethod);
    const [consent, setConsent] = useState(initialConsent);
    const [busy, setBusy] = useState(false);
    const [code, setCode] = useState('');
    const [codeFocus, setCodeFocus] = useState(false);
    const [codeError, setCodeError] = useState('');
    const [sendError, setSendError] = useState('');

    const [codeRequested, setCodeRequested] = useState(false);

    useEffect(() => {
        if (open) {
            setMethod(initialMethod);
            setConsent(initialConsent);
        }
    }, [open, initialMethod, initialConsent]);

    const validCode = code.length === 6;
    const consentMissing = method === 'whatsApp' && !consent;

    const messageForStatus = (httpStatus, fallback) => {
        if (httpStatus === 403) return i18n.t('phonenumbermismatch');
        if (httpStatus === 404) return i18n.t('usernotfound');
        return fallback;
    };

    const handleMethod = (event) => {
        setMethod(event.target.value);
        setConsent(false);
    };

    const handleSend = () => {
        setBusy(true);
        setSendError('');
        setCodeError('');

        const payload = {
            email: email,
            phone: phone,
            prefferedLanguage: preferredLanguage,
            verificationMethod: method,
            whatsApp: method === 'whatsApp',
            whatsAppConsent: consent
        };

        axiosInstance.post("/api/User/sendPhoneVerificationCode", payload)
            .then(() => {
                setCode('');
                setCodeRequested(true);
                setStatus('sent');
            })
            .catch((error) => {
                console.error(error);
                const httpStatus = error.response?.status;
                setStatus('sendFailed');
                setSendError(messageForStatus(
                    httpStatus,
                    httpStatus === 400 ? i18n.t('sendcodeunexpected') : i18n.t('verificationcodesentfailure')
                ));
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const handleVerify = () => {
        setBusy(true);
        setCodeError('');

        const payload = {
            ...extraVerifyPayload,
            email: email,
            phone: phone,
            phoneVerifyCode: code,
            isPhoneVerified: true
        };

        axiosInstance.post("/api/User/verifyPhoneCode", payload)
            .then(() => {
                setStatus('verified');
                onVerified?.();
            })
            .catch((error) => {
                console.error(error);
                const httpStatus = error.response?.status;
                setCodeError(messageForStatus(httpStatus, i18n.t('codeinvalidorexpired')));
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const handleClose = () => {
        setStatus('idle');
        setCode('');
        setCodeRequested(false);
        setCodeError('');
        setSendError('');
        setMethod(initialMethod);
        setConsent(initialConsent);
        onClose?.();
    };

    const dialogTitle = () => {
        if (status === 'verified') return i18n.t('codeverified');
        if (status === 'sent') return i18n.t('verificationcodesent');
        if (status === 'sendFailed') return i18n.t('verificationcodesentfailure');
        return i18n.t('verifyphone');
    };

    return (
        <Dialog open={open} fullWidth maxWidth="xs">
            <DialogTitle>{dialogTitle()}</DialogTitle>
            <DialogContent>
                {status === 'verified' ? (
                    <Typography>{i18n.t('phoneverifiedsuccess')}</Typography>
                ) : (
                    <Stack spacing={2}>
                        {showMethodPicker ? (
                            <FormControl>
                                <FormLabel id="verify-method-radio-buttons-group">{i18n.t('sendcodevia')}</FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby="verify-method-radio-buttons-group"
                                    name="verifyMethod"
                                    value={method}
                                    onChange={handleMethod}
                                >
                                    <FormControlLabel value="sms" control={<Radio />} label={i18n.t('textmessage')} />
                                    <FormControlLabel value="whatsApp" control={<Radio />} label={i18n.t('whatsapp')} />
                                </RadioGroup>
                            </FormControl>
                        ) : (
                            <Typography variant="body2">
                                {i18n.t('sendcodevia')}: {method === 'whatsApp' ? i18n.t('whatsapp') : i18n.t('textmessage')}
                            </Typography>
                        )}

                        {showMethodPicker && method === 'whatsApp' && (
                            <FormControlLabel
                                control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
                                label={<Typography variant="body2">{i18n.t('whatsappconsentcheckbox')}</Typography>}
                            />
                        )}

                        {sendError && (
                            <Alert severity="error">{sendError}</Alert>
                        )}

                        {codeRequested ? (
                            <>
                                <Typography>{i18n.t('codesentcheckmessages')}</Typography>
                                <TextField
                                    required
                                    fullWidth
                                    id="phoneCode"
                                    label={i18n.t('phoneverificationcode')}
                                    name="phoneCode"
                                    value={code}
                                    error={Boolean(codeError) || (!validCode && codeFocus)}
                                    helperText={codeError || (!validCode && codeFocus ? i18n.t('enterverificationcode') : "")}
                                    onFocus={() => setCodeFocus(true)}
                                    onBlur={() => setCodeFocus(false)}
                                    onChange={(e) => {
                                        setCode(e.target.value);
                                        setCodeError('');
                                    }}
                                />
                            </>
                        ) : (
                            <Typography>{i18n.t('verifyphoneprompt')}</Typography>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {status === 'verified' ? (
                    <Button onClick={handleClose} color="primary">
                        {i18n.t('close')}
                    </Button>
                ) : (
                    <>
                        {allowClose && (
                            <Button onClick={handleClose} color="primary" disabled={busy}>
                                {closeLabel ?? i18n.t('cancel')}
                            </Button>
                        )}
                        <Button onClick={handleSend} color="primary" disabled={busy || consentMissing}>
                            {codeRequested ? i18n.t('resendcode') : i18n.t('sendcode')}
                        </Button>
                        {codeRequested && (
                            <Button onClick={handleVerify} color="primary" variant="contained" disabled={busy || !validCode}>
                                {busy ? <CircularProgress size={20} /> : i18n.t('confirm')}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
